import "core-js/modules/es.array.push.js";
import { featureReduce } from '@turf/meta';
import union from '@turf/union';
import cx from 'classnames';
import ModalActionBar from "../../core/components/Modal/ModalActionBar";
import ModalHeader from "../../core/components/Modal/ModalHeader";
import { LimitType } from 'legoland-sdk/dist/experimental';
import { Preloader } from 'llui';
import moment from 'moment';
import { useProductLimitSchemas } from "../../products/productsHooks";
import { useEffect, useMemo, useState } from 'react';
import { Button, useToasts } from 'tombac';
import { useUpdateContractProductLimitMutation } from "../contractsApiHooks";
import "./LimitsConfigurator.css";
import { ProductLogo } from "./ProductLogo";
import { getLimitEditorComponent } from "./limit-editors";
import { initLoadedGeoLimit } from "./limit-editors/GeoLimitEditor";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const afterLoadProcessors = {
  /* Converts MultiPolygon limit to FeatureCollection consumed by GeoLimitEditor component. */
  [LimitType.Geo]: limit => {
    try {
      return initLoadedGeoLimit(limit);
    } catch (error) {
      console.error(error);
      return null;
    }
  }
};
const convertCoords = limit => {
  const newCords = limit.features.map(feature => {
    const cords = feature.geometry.coordinates[0];
    const convertedCords = cords.map(pointCord => {
      const [lng, lat] = pointCord;
      return lng > 180 ? [lng - 360, lat] : lng < -180 ? [360 - Math.abs(lng), lat] : pointCord;
    });
    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: [convertedCords]
      }
    };
  });
  return {
    ...limit,
    features: newCords
  };
};
const beforeSaveProcessors = {
  [LimitType.DateRange]: limit => limit && {
    from: limit?.from && moment(limit.from).format('YYYY-MM-DD'),
    to: limit?.to && moment(limit.to).format('YYYY-MM-DD')
  },
  /* Convert FeatureCollection output from GeoLimitEditor to MultiPolygon limit. */
  [LimitType.Geo]: limit => {
    if (limit) {
      const newLimit = convertCoords(limit);
      return featureReduce(newLimit, (prev, curr) => !prev ? curr : union(prev, curr))?.geometry ?? null;
    }
  }
};
function NoOpComponent(_ref) {
  let {
    children
  } = _ref;
  return children;
}
export default function LimitsConfigurator(_ref2) {
  let {
    className,
    onRequestClose,
    productValues,
    contractId,
    packages
  } = _ref2;
  const defaults = productValues.limits;
  const schemas = useProductLimitSchemas({
    productId: productValues.product.id
  });
  const {
    mutateAsync: updateContractProductLimits
  } = useUpdateContractProductLimitMutation();
  const loadedLimits = useMemo(() => productValues.overrides, [JSON.stringify(productValues.overrides)]);
  const sharedLimits = packages?.limits;
  const [limits, setLimits] = useState(null);
  useEffect(() => setLimits(afterLoadLimits(loadedLimits)), [loadedLimits, schemas]);
  const handleLimitChange = (limitName, value) => {
    setLimits({
      ...limits,
      [limitName]: value
    });
  };
  const {
    addToast
  } = useToasts();
  const validateRegions = () => {
    const allowedArea = limits?.allowedArea;
    if (allowedArea) {
      return allowedArea.features.some(feature => feature.properties.isCrossedMeridian === true);
    }
    return false;
  };
  const handleSave = async () => {
    try {
      await updateContractProductLimits({
        contractId: contractId,
        productId: productValues.product.id,
        limits: {
          overrides: {
            ...beforeSaveLimits(limits)
          }
        }
      });
      addToast('Limits are set', 'success');
      onRequestClose();
    } catch (error) {
      addToast('Something went wrong', 'danger');
      onRequestClose();
      console.error(error);
    }
  };
  function afterLoadLimits(limits) {
    if (!limits) return null;
    const processedLimits = {};
    schemas.forEach(_ref3 => {
      let {
        name: limitName,
        type: limitType
      } = _ref3;
      const limit = limits[limitName];
      processedLimits[limitName] = limit && afterLoadProcessors[limitType] ? afterLoadProcessors[limitType](limit) : limit;
    });
    return processedLimits;
  }
  function beforeSaveLimits(limits) {
    if (!limits) return null;
    const processedLimits = {};
    schemas.forEach(_ref4 => {
      let {
        name: limitName,
        type: limitType
      } = _ref4;
      if (sharedLimits && Object.hasOwn(sharedLimits, limitName)) return;
      const limit = limits[limitName];
      processedLimits[limitName] = beforeSaveProcessors[limitType] ? beforeSaveProcessors[limitType](limit) : limit;
    });
    return processedLimits;
  }
  const limitComponents = [];
  let ExtendedEditor = null;
  let extendedEditorProps;
  let EditorContextProvider = NoOpComponent;
  if (limits) {
    schemas.forEach(schema => {
      const LimitComponent = getLimitEditorComponent(schema.type);
      limitComponents.push(LimitComponent);
      if (LimitComponent.ExtendedEditor) {
        ExtendedEditor = LimitComponent.ExtendedEditor;
        extendedEditorProps = {
          defaultLimit: defaults[schema.name],
          label: schema.label,
          limit: limits[schema.name],
          onChange: value => handleLimitChange(schema.name, value),
          schema
        };
      }
      if (LimitComponent.ContextProvider) {
        EditorContextProvider = LimitComponent.ContextProvider;
      }
    });
  }
  const loading = false;
  return /*#__PURE__*/_jsx("section", {
    className: cx('LimitsConfigurator', ExtendedEditor && 'LimitsConfigurator--extended', className),
    "data-sentry-component": "LimitsConfigurator",
    "data-sentry-source-file": "LimitsConfigurator.tsx",
    children: /*#__PURE__*/_jsxs(EditorContextProvider, {
      "data-sentry-element": "EditorContextProvider",
      "data-sentry-source-file": "LimitsConfigurator.tsx",
      "data-sentry-element": "EditorContextProvider",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "LimitsConfigurator__main-pane",
        children: [/*#__PURE__*/_jsx(ModalHeader, {
          image: /*#__PURE__*/_jsx(ProductLogo, {
            productId: productValues.product.id
          }),
          primaryTitle: "Set usage limits",
          secondaryTitle: productValues.product.displayName,
          tag: "header",
          "data-sentry-element": "ModalHeader",
          "data-sentry-source-file": "LimitsConfigurator.tsx",
          "data-sentry-element": "ModalHeader"
        }), loading ? /*#__PURE__*/_jsx(Preloader, {}) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("ul", {
            className: "LimitsConfigurator__limits",
            children: limitComponents.map((LimitComponent, index) => {
              if (LimitComponent === null) return null;
              const schema = schemas[index];
              return /*#__PURE__*/_jsx("li", {
                className: "LimitsConfigurator__limit-item",
                children: /*#__PURE__*/_jsx(LimitComponent, {
                  defaultLimit: defaults[schema.name],
                  isSharedWithOther: sharedLimits && Object.hasOwn(sharedLimits, schema.name),
                  label: schema.label,
                  limit: (sharedLimits && sharedLimits[schema.name]) ?? limits[schema.name],
                  onChange: value => handleLimitChange(schema.name, value),
                  schema: schema
                })
              }, schema.name);
            })
          }), /*#__PURE__*/_jsxs(ModalActionBar, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: onRequestClose,
              variant: "flat",
              children: "Cancel"
            }), /*#__PURE__*/_jsx(Button, {
              $ml: "1sp",
              disabled: validateRegions(),
              onClick: handleSave,
              variant: "primary",
              children: "Save"
            })]
          })]
        })]
      }), ExtendedEditor && /*#__PURE__*/_jsx("div", {
        className: "LimitsConfigurator__extended-pane",
        children: /*#__PURE__*/_jsx(ExtendedEditor, {
          ...extendedEditorProps
        })
      })]
    })
  });
}